import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface RecurringSchedule {
  frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string;
  timezone: string;
  endDate?: string;
  maxOccurrences?: number;
}

export interface ScheduleOptimization {
  suggestedTimes: Array<{
    time: string;
    dayOfWeek: string;
    score: number;
    reason: string;
  }>;
  bestDay: string;
  bestTime: string;
  engagementPrediction: number;
}

const advancedSchedulingService = {
  /**
   * Schedule a post for future publication
   */
  schedulePost: async (params: {
    postId: string;
    publishedAt: string;
    timezone?: string;
  }): Promise<any> => {
    const response = await axios.post(
      `${API_URL}/api/scheduling/schedule`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.post;
  },

  /**
   * Create recurring post schedule
   */
  createRecurring: async (params: {
    templateId?: string;
    content: string;
    title?: string;
    recurringSchedule: RecurringSchedule;
    tierIds?: string[];
    tags?: string[];
  }): Promise<any> => {
    const response = await axios.post(
      `${API_URL}/api/scheduling/recurring`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  /**
   * Get scheduled posts for calendar view
   */
  getCalendar: async (startDate: string, endDate: string): Promise<any> => {
    const response = await axios.get(`${API_URL}/api/scheduling/calendar`, {
      params: { startDate, endDate },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  /**
   * Get schedule optimization suggestions
   */
  getOptimization: async (): Promise<ScheduleOptimization> => {
    const response = await axios.get(`${API_URL}/api/scheduling/optimization`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.optimization;
  },

  /**
   * Reschedule a post
   */
  reschedulePost: async (postId: string, newPublishedAt: string): Promise<any> => {
    const response = await axios.put(
      `${API_URL}/api/scheduling/reschedule`,
      { postId, newPublishedAt },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.post;
  },

  /**
   * Cancel scheduled post
   */
  cancelScheduledPost: async (postId: string): Promise<any> => {
    const response = await axios.delete(
      `${API_URL}/api/scheduling/${postId}/cancel`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.post;
  },
};

export default advancedSchedulingService;
